import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Field, Input } from '@w7/ui'
import { getAuthConfig, isOidcConfigured } from '@/auth/config'
import {
  beginOidcLogin,
  clearAllAuth,
  isDemoSession,
  rememberNext,
  resolveAuthPhase,
  startDemoSession,
} from '@/auth/oidc'
import {
  LOGIN_ATTEMPT_LIMIT,
  LOGIN_LOCK_MS,
  normalizeUsername,
  usernameErrorMessage,
  validateUsername,
  type UsernameIssue,
} from '@/auth/validate'
import { LoginStage } from './LoginStage'
import { W7Mark } from './W7Mark'
import './auth.css'

/**
 * Minimal agent-console login — OIDC PKCE (password on Keycloak only).
 */

export function LoginPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const cfg = useMemo(() => getAuthConfig(), [])
  const oidcReady = isOidcConfigured(cfg)

  const [username, setUsername] = useState('')
  const [touched, setTouched] = useState(false)
  const [issue, setIssue] = useState<UsernameIssue | null>(null)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [attempts, setAttempts] = useState(0)
  const [lockedUntil, setLockedUntil] = useState(0)
  const [now, setNow] = useState(Date.now())

  const next = params.get('next') || '/'
  const reason = params.get('reason')

  useEffect(() => {
    const phase = resolveAuthPhase()
    if (phase.phase === 'ready') navigate(next.startsWith('/') ? next : '/', { replace: true })
    if (phase.phase === 'no-access') navigate('/no-access', { replace: true })
  }, [navigate, next])

  useEffect(() => {
    if (reason === 'expired') setFormError('Session expired. Sign in again.')
    else if (reason === 'denied') setFormError('Sign-in was cancelled.')
    else if (reason === 'error') setFormError('Sign-in failed. Try again.')
    if (reason) setBusy(false)
  }, [reason])

  /* Continue sets busy then leaves for Keycloak. Browser-back / bfcache can
     restore this page with busy still true — always clear when we are visible again. */
  useEffect(() => {
    const clearBusy = () => setBusy(false)
    clearBusy()
    const onPageShow = () => clearBusy()
    const onVisible = () => {
      if (document.visibilityState === 'visible') clearBusy()
    }
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('focus', clearBusy)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('focus', clearBusy)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  useEffect(() => {
    if (lockedUntil <= Date.now()) return
    const t = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(t)
  }, [lockedUntil])

  const locked = lockedUntil > now
  const lockSeconds = Math.max(0, Math.ceil((lockedUntil - now) / 1000))

  const runValidation = (value: string): UsernameIssue | null => {
    const nextIssue = validateUsername(value)
    setIssue(nextIssue)
    return nextIssue
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    setFormError(null)
    if (locked) return

    const err = runValidation(username)
    if (err) {
      const nextAttempts = attempts + 1
      setAttempts(nextAttempts)
      if (nextAttempts >= LOGIN_ATTEMPT_LIMIT) {
        setLockedUntil(Date.now() + LOGIN_LOCK_MS)
        setAttempts(0)
        setFormError(`Too many attempts. Try again in ${LOGIN_LOCK_MS / 1000}s.`)
      }
      return
    }

    if (!oidcReady) {
      setFormError('Sign-in is unavailable. Use demo access or configure Keycloak.')
      return
    }

    setBusy(true)
    rememberNext(next)
    try {
      await beginOidcLogin(normalizeUsername(username))
      /* location.assign should unload us; if we are still here, unlock. */
      setBusy(false)
    } catch (ex) {
      setBusy(false)
      setFormError(ex instanceof Error ? ex.message : 'Sign-in could not start.')
    }
  }

  const continueDemo = () => {
    if (!cfg.allowDemoAuth) return
    clearAllAuth()
    startDemoSession()
    rememberNext(next)
    navigate(next.startsWith('/') ? next : '/', { replace: true })
  }

  if (isDemoSession() && resolveAuthPhase().phase === 'ready') {
    return <Navigate to={next.startsWith('/') ? next : '/'} replace />
  }

  return (
    <div className="w7-auth" data-testid="login-screen">
      <LoginStage />
      <div className="w7-auth-panel">
        <div className="w7-auth-panel-bg" aria-hidden="true">
          <img className="w7-auth-panel-bg-a" src="/auth/editorial/form-ai-bg.png" alt="" decoding="async" />
          <img className="w7-auth-panel-bg-b" src="/auth/editorial/form-ai-bg.png" alt="" decoding="async" />
          <div className="w7-auth-panel-veil" />
        </div>
        <div className="w7-auth-card">
          <div className="w7-auth-brand">
            <W7Mark className="w7-auth-mark-word" size={48} />
            <span className="w7-auth-brand-name">Ops console</span>
          </div>

          <p className="w7-auth-eyebrow">Welcome back</p>
          <h1 className="w7-auth-title">Sign in to continue</h1>
          <p className="w7-auth-lede">Access your operations console — password on the next secure step</p>

          {formError ? (
            <p className="w7-auth-alert" role="alert">
              {formError}
            </p>
          ) : null}

          <form className="w7-auth-form" onSubmit={(e) => void onSubmit(e)} noValidate>
            <Field
              label="Email or username"
              required
              error={touched && issue ? usernameErrorMessage(issue) : undefined}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  name="username"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  value={username}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  disabled={busy || locked}
                  placeholder="Enter your email or username"
                  onBlur={() => {
                    setTouched(true)
                    runValidation(username)
                  }}
                  onChange={(ev) => {
                    setUsername(ev.target.value)
                    if (touched) runValidation(ev.target.value)
                    if (formError) setFormError(null)
                  }}
                />
              )}
            </Field>

            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={busy || locked}
              data-testid="login-submit"
              icon="arrow-right"
            >
              {locked ? `Retry in ${lockSeconds}s` : 'Continue'}
            </Button>
          </form>

          {cfg.allowDemoAuth ? (
            <>
              <div className="w7-auth-divider" aria-hidden="true">
                or
              </div>
              <button
                type="button"
                className="w7-auth-demo"
                data-testid="login-demo"
                onClick={continueDemo}
              >
                Continue with demo
              </button>
            </>
          ) : null}

          <p className="w7-auth-secure">Secure. Reliable. Built for scale.</p>
        </div>
      </div>
    </div>
  )
}
